/**
 * Created by christiancueni on 29/07/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import { Component, ChangeDetectionStrategy, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { ViewEvent } from "./event";
import {HelloclassRRuleService} from "../services/rrule.service";

const SLOTS_LABELS = ["08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", '16:00', '17:00'];

@Component({
    selector: 'hc-event',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
            <div (click)="eventClicked.emit()"
                class="event event--start-{{event.start}} event--length-{{event.duration}} col-xs-12"
                [ngClass]="classDict"
                data-cy="event">
                    <div class="event__colorbar" [ngClass]="'bg-' + event.labelColor"></div>
                        <div class="event__wrapper">
                        <span class="fade-out__gradient"></span>
                        <span class="fade-out__gradient fade-out__gradient--bottom fade-out__gradient--margin-left"></span>
                        <div class="event__content">
                            <div class="event__information">
                                <div class="event__header">
                                    <p class="event__group text-gray text-size-small">
                                        <span *ngIf="isDetail && event.labelTitle" class="event__label" [ngClass]="'event__label--' + event.labelColor">
                                        {{ event.labelTitle }}
                                        </span>{{ event.groupDetails.displayName }}
                                    </p>
                                    <div *ngIf="isDetail && canEditEvents" class="event__edit edit-actions">
                                        <button
                                            class="unstyled-button edit-actions__action"
                                            data-cy="editEvent"
                                            (click)="editEvent()">
                                            <span class="icon-pen icon-size edit-actions__icon"></span>
                                        </button>
                                        <button
                                            class="unstyled-button edit-actions__action"
                                            aria-label="Delete"
                                            (click)="deleteEvent()"
                                            data-cy="deleteEvent">
                                            <span class="icon-bin icon-size edit-actions__icon"></span>
                                        </button>
                                    </div>
                                    <div *ngIf="numEvents > 1" class="event__navigation small">
                                        <button class="icon-arrow-left unstyled-button " (click)="showPrev($event, dayIndex, event.start)"></button>
                                        <p class="event__pagination">{{displayIndex}}/{{numEvents}}</p>
                                        <button class="icon-arrow-right unstyled-button " (click)="showNext($event, dayIndex, event.start)"></button>
                                    </div>
                                </div>
                                <div *ngIf="!isDetail">
                                    <h3 class="event__title fade-out qa-event-title h4" *ngIf="event.title !== ''">
                                        {{event.title}}
                                    </h3>
                                    <h3 class="event__title fade-out qa-event-title h4" *ngIf="event.title === ''">
                                        {{event.description}}
                                    </h3>
                                    <p class="event__description fade-out" *ngIf="event.title !== ''" [innerHtml]="addNewLines(event.description)"></p>
                                </div>
                                <div *ngIf="isDetail">
                                    <h2 class="event__title h3" *ngIf="event.title !== ''">{{event.title}}</h2>
                                    <h2 class="event__title h3" *ngIf="event.title === ''">{{event.description}}</h2>
                                    <p class="event__description" *ngIf="event.title !== ''" [innerHtml]="addNewLines(event.description) | linky"></p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="event__info" [ngClass]="{'event__info--overview': !isDetail}">
                        <div class="event__action" *ngIf="canEditEvents">
                            <button class="btn btn--gray btn--innershadow text-size-small" *ngIf="event.state === 'draft'" (click)="publishEvent($event, event)">{{'Veröffentlichen' | translate}}</button>
                        </div>
                        <ul class="event__meta meta-list event-meta-list" *ngIf="event.state !== 'draft'">
                            <li *ngIf="isDetail" class="meta-list__item event-meta-list__item event-meta-list__item--date">
                                <p class="text-size-small text-gray"><span class="icon-calendar"></span>{{event.date |dateFormatFilter:'dddd, DD. MMMM YYYY'}}</p>
                                <p  *ngIf="event.recurrences"
                                    data-cy="event-detail-recurrences-text"
                                    class="text-size-small text-gray no-margin">
                                    {{rruleService.getFrequencyAsText(event.recurrences, event.date) | async}}</p>
                            </li>
                            <li *ngIf="!isDetail && event.displayCommentCount()" class="meta-list__item event-meta-list__item event-meta-list__item--comments">
                                <p class="text-size-small text-gray no-margin"><span class="icon-speechbubble"></span>{{event.countComments()}}</p>
                            </li>
                            <li *ngIf="event.isPrivate" class="meta-list__item event-meta-list__item event-meta-list__item--privacy">
                                <p class="text-size-small text-gray no-margin"><span class="icon-lock"></span></p>
                            </li>
                        </ul>
                    </div>
                    <hc-event-attachment *ngIf="isDetail && isOnline" class="event__attachments" [uploads]="event.uploads" [labelColor]="event.labelColor"></hc-event-attachment>
                </div>
    `,
})
export class EventComponent {

    @Input() event: ViewEvent;
    @Input() isHidden: boolean;
    @Input() canEditEvents = false;
    @Input() isDetail = false;
    @Input() numEvents = 1;
    @Input() dayIndex: number;
    @Input() displayIndex: number;
    @Input() isOnline = true;
    @Output() showPrevEvent = new EventEmitter();
    @Output() showNextEvent = new EventEmitter();
    @Output() publish = new EventEmitter();
    @Output() eventClicked = new EventEmitter();
    @Output() delete = new EventEmitter();
    @Output() edit = new EventEmitter();

    public timeSlot = SLOTS_LABELS;

    public classDict: Object = {};

    constructor (public rruleService: HelloclassRRuleService) {
    }

    ngOnInit() {
        this.updateClassDict();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.updateClassDict();
    }

    private updateClassDict() {
        // ... https://github.com/angular/angular.js/issues/9109
        this.classDict = {
            'event': true,
            'event--detail': this.isDetail,
            'col-xs-12': true,
            'hidden-lg hidden-md': this.isHidden,
        };

        if (!this.isDetail) {
            this.classDict['event--start--' + this.event.start] = true;
            this.classDict['event--length--' + this.event.duration] = true;
        } else {
            this.classDict['event--start--' + this.event.start] = false;
            this.classDict['event--length--' + this.event.duration] = false;
        }
    }

    public publishEvent(clickEvent, event) {
        clickEvent.stopPropagation();
        this.publish.emit(event);
    }

    public showPrev(clickEvent, x: number, y: number) {
        clickEvent.stopPropagation();
        this.showPrevEvent.emit({ dayIndex: x, startIndex: y });
    }

    public showNext(clickEvent, x: number, y: number) {
        clickEvent.stopPropagation();
        this.showNextEvent.emit({ dayIndex: x, startIndex: y });
    }

    public addNewLines(text: string) {
        return text.replace(/\n/g, '<br />');
    }

    public deleteEvent(): void {
        this.delete.emit(this.event);
    }

    public editEvent(): void {
        this.edit.emit(this.event);
    }
}
